import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Icon } from "./Icon";

export interface ZonaPeligroProps {
  /**
   * Glifo de Material Symbols. `delete_outline` para la papelera — el DS usa el
   * contorno y no el relleno: es la acción reversible.
   */
  icono?: string;
  /** La ACCIÓN, no el estado: "Mover a la papelera", no "Papelera". */
  titulo: string;
  /**
   * Qué pasa y qué NO pasa. Va como nodo y no como string porque la parte que
   * importa —"no se borra"— va en 600, y el DS pide esa palabra destacada
   * dentro de la frase. Ver `NoSeBorra`.
   */
  descripcion: React.ReactNode;
  /** Texto del botón. Suele repetir el título, y está bien: el título explica
   * la sección y el botón confirma qué se aprieta. */
  accion: string;
  onAccion: () => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Zona de peligro del final de una ficha (DS §4bis.6).
 *
 * Panel `#FEF2F2` con borde `#FECACA` y botón OUTLINE rojo — nunca rojo sólido.
 * La razón es de producto y no de estética: en esta app "inactivar" y "mover a
 * la papelera" son acciones reversibles que CONSERVAN los documentos y las
 * firmas de terreno. Un botón rojo sólido promete una destrucción que no
 * ocurre, y entrena al usuario a temerle a un botón inofensivo — con lo cual
 * deja de creerle al rojo el día que sí signifique algo.
 *
 * Por eso la descripción es obligatoria y siempre dice qué se conserva: es la
 * mitad del componente, no un texto de relleno.
 *
 * Se monta FUERA del `<form>` de la ficha a propósito. Mover a la papelera no
 * es "guardar los datos", y adentro un Enter en cualquier campo quedaría a un
 * tab de distancia de la acción destructiva.
 */
export function ZonaPeligro({
  icono = "delete_outline",
  titulo,
  descripcion,
  accion,
  onAccion,
  disabled = false,
  className,
}: ZonaPeligroProps) {
  return (
    <section
      className={cn(
        "border-destructive-border bg-destructive-bg flex flex-wrap items-start gap-[11px] rounded-xl border px-3.5 py-3.5",
        className,
      )}
    >
      <Icon name={icono} size={19} className="text-destructive mt-px shrink-0" />
      <div className="flex min-w-[200px] flex-1 flex-col gap-[3px]">
        <span className="text-destructive text-[13px] font-semibold">{titulo}</span>
        <span className="text-muted-foreground text-[11.5px] leading-[1.5]">{descripcion}</span>
      </div>
      <Button variant="destructive-soft" size="sm" disabled={disabled} onClick={onAccion}>
        <Icon name={icono} size={17} />
        {accion}
      </Button>
    </section>
  );
}

/**
 * "no se borra" en 600 dentro de la descripción. Existe como componente y no
 * como un `<strong>` suelto en cada ficha porque es LA frase que hace que el
 * panel rojo no asuste: si una ficha la escribe distinta, esa ficha miente
 * sobre lo que hace el botón.
 */
export function NoSeBorra() {
  return <strong className="font-semibold">no se borra</strong>;
}
